import React, { useState } from 'react';
import {
  BookOpen,
  Plug,
  LayoutDashboard,
  Bot,
  Settings,
  ChevronRight,
  ExternalLink,
  Users,
  Building,
  Globe,
  CreditCard,
  Bell,
  BarChart2,
  Search,
  Calendar,
  Filter,
  Layers,
  Zap,
  Shield,
  Database,
  FileText,
  Target,
  TrendingUp,
  Play,
  CheckCircle
} from 'lucide-react';
import './HelpCenter.css';

const HelpCenter = () => {
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [activeSection, setActiveSection] = useState('overview');

  const categories = [
    {
      id: 'getting-started',
      label: 'Démarrage',
      icon: Play,
      sections: [
        { id: 'overview', label: 'Vue d\'ensemble' },
        { id: 'connectors', label: 'Ajouter des connecteurs' },
        { id: 'accounts', label: 'Gérer les comptes' },
        { id: 'sites', label: 'Créer des sites' },
        { id: 'clients', label: 'Gérer les clients' }
      ]
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      sections: [
        { id: 'navigation', label: 'Navigation' },
        { id: 'metrics', label: 'Comprendre les métriques' },
        { id: 'filters', label: 'Filtres et périodes' },
        { id: 'platforms', label: 'Plateformes' }
      ]
    },
    {
      id: 'mcp',
      label: 'MCP',
      icon: Bot,
      sections: [
        { id: 'what-is-mcp', label: 'Qu\'est-ce que le MCP ?' },
        { id: 'setup', label: 'Configuration' },
        { id: 'tools', label: 'Outils disponibles' },
        { id: 'write-operations', label: 'Opérations d\'écriture' }
      ]
    },
    {
      id: 'management',
      label: 'Gestion',
      icon: Settings,
      sections: [
        { id: 'subscription', label: 'Abonnement' },
        { id: 'statistics', label: 'Statistiques' },
        { id: 'notifications', label: 'Notifications' },
        { id: 'team', label: 'Équipe et organisation' }
      ]
    }
  ];

  const renderContent = () => {
    const key = `${activeCategory}-${activeSection}`;

    switch (key) {
      // DÉMARRAGE
      case 'getting-started-overview':
        return (
          <div className="help-content">
            <h1>Bienvenue sur Kokpit</h1>
            <p className="help-intro">
              Kokpit est votre plateforme centralisée pour gérer et analyser vos campagnes publicitaires
              sur Google Ads, Microsoft Ads et Meta Ads.
            </p>

            <div className="help-cards">
              <div className="help-card" onClick={() => { setActiveCategory('getting-started'); setActiveSection('connectors'); }}>
                <Plug size={24} />
                <h3>Connecter vos comptes</h3>
                <p>Liez vos comptes publicitaires pour commencer</p>
              </div>
              <div className="help-card" onClick={() => { setActiveCategory('dashboard'); setActiveSection('navigation'); }}>
                <LayoutDashboard size={24} />
                <h3>Explorer le Dashboard</h3>
                <p>Visualisez vos performances en un coup d'œil</p>
              </div>
              <div className="help-card" onClick={() => { setActiveCategory('mcp'); setActiveSection('what-is-mcp'); }}>
                <Bot size={24} />
                <h3>Découvrir le MCP</h3>
                <p>Utilisez Claude pour analyser vos données</p>
              </div>
            </div>

            <h2>Premiers pas</h2>
            <ol className="help-steps">
              <li>
                <CheckCircle size={20} className="step-icon" />
                <div>
                  <strong>Créez votre compte</strong>
                  <p>Inscrivez-vous et choisissez votre plan d'abonnement</p>
                </div>
              </li>
              <li>
                <CheckCircle size={20} className="step-icon" />
                <div>
                  <strong>Connectez vos plateformes publicitaires</strong>
                  <p>Google Ads, Microsoft Ads, Meta Ads - connectez-les en un clic</p>
                </div>
              </li>
              <li>
                <CheckCircle size={20} className="step-icon" />
                <div>
                  <strong>Organisez vos données</strong>
                  <p>Créez des clients et des sites pour structurer vos comptes</p>
                </div>
              </li>
              <li>
                <CheckCircle size={20} className="step-icon" />
                <div>
                  <strong>Analysez vos performances</strong>
                  <p>Utilisez le Dashboard et le MCP pour obtenir des insights</p>
                </div>
              </li>
            </ol>
          </div>
        );

      case 'getting-started-connectors':
        return (
          <div className="help-content">
            <h1>Ajouter des connecteurs</h1>
            <p className="help-intro">
              Les connecteurs permettent de lier vos comptes publicitaires à Kokpit pour synchroniser vos données.
            </p>

            <h2>Plateformes supportées</h2>
            <div className="help-platform-list">
              <div className="help-platform-item">
                <img src="/logos/google-ads.svg" alt="Google Ads" />
                <div>
                  <h3>Google Ads</h3>
                  <p>Connectez vos comptes Google Ads via OAuth. Accédez à toutes vos campagnes, groupes d'annonces, mots-clés et métriques.</p>
                </div>
              </div>
              <div className="help-platform-item">
                <img src="/logos/microsoft-ads.svg" alt="Microsoft Ads" />
                <div>
                  <h3>Microsoft Ads</h3>
                  <p>Synchronisez vos comptes Microsoft Advertising (anciennement Bing Ads) pour une vue unifiée.</p>
                </div>
              </div>
              <div className="help-platform-item">
                <img src="/logos/meta-ads.png" alt="Meta Ads" />
                <div>
                  <h3>Meta Ads</h3>
                  <p>Intégrez Facebook Ads et Instagram Ads pour suivre vos campagnes sociales.</p>
                </div>
              </div>
              <div className="help-platform-item">
                <img src="/logos/google-search-console.png" alt="Google Search Console" />
                <div>
                  <h3>Google Search Console</h3>
                  <p>Suivez vos performances SEO, mots-clés et positions dans les résultats de recherche Google.</p>
                </div>
              </div>
              <div className="help-platform-item">
                <img src="/logos/google-merchant.png" alt="Google Merchant Center" />
                <div>
                  <h3>Google Merchant Center</h3>
                  <p>Gérez vos flux produits et synchronisez vos données Shopping avec vos campagnes.</p>
                </div>
              </div>
              <div className="help-platform-item">
                <img src="/logos/google-analytics.png" alt="Google Analytics 4" />
                <div>
                  <h3>Google Analytics 4</h3>
                  <p>Analysez le comportement des utilisateurs sur votre site et croisez avec vos données publicitaires.</p>
                </div>
              </div>
              <div className="help-platform-item">
                <img src="/logos/matomo.png" alt="Matomo Analytics" />
                <div>
                  <h3>Matomo Analytics</h3>
                  <p>Alternative open-source à Google Analytics, respectueuse de la vie privée.</p>
                </div>
              </div>
              <div className="help-platform-item">
                <img src="/logos/piano-analytics.png" alt="Piano Analytics" />
                <div>
                  <h3>Piano Analytics</h3>
                  <p>Solution analytics premium pour les éditeurs et médias (anciennement AT Internet).</p>
                </div>
              </div>
            </div>

            <h2>Comment ajouter un connecteur</h2>
            <ol className="help-numbered-list">
              <li>Cliquez sur <strong>Connecteurs</strong> dans le menu principal</li>
              <li>Sélectionnez la plateforme que vous souhaitez connecter</li>
              <li>Cliquez sur <strong>Connecter</strong></li>
              <li>Autorisez Kokpit à accéder à vos données publicitaires</li>
              <li>Sélectionnez les comptes à synchroniser</li>
            </ol>

            <div className="help-tip">
              <Zap size={20} />
              <div>
                <strong>Conseil</strong>
                <p>Vous pouvez connecter plusieurs comptes de la même plateforme. Chaque compte apparaîtra séparément dans votre bibliothèque.</p>
              </div>
            </div>
          </div>
        );

      case 'getting-started-accounts':
        return (
          <div className="help-content">
            <h1>Gérer les comptes publicitaires</h1>
            <p className="help-intro">
              Une fois vos connecteurs configurés, vos comptes publicitaires apparaissent dans la bibliothèque.
            </p>

            <h2>Bibliothèque de comptes</h2>
            <p>
              La bibliothèque centralise tous vos comptes connectés. Vous pouvez y accéder via le menu
              <strong> Connecteurs</strong> puis l'onglet <strong>Bibliothèque</strong>.
            </p>

            <h2>Actions disponibles</h2>
            <ul className="help-feature-list">
              <li>
                <Database size={18} />
                <div>
                  <strong>Voir les détails</strong>
                  <p>Consultez les informations du compte (ID, nom, devise, statut)</p>
                </div>
              </li>
              <li>
                <Building size={18} />
                <div>
                  <strong>Associer à un client</strong>
                  <p>Rattachez le compte à un client existant pour une meilleure organisation</p>
                </div>
              </li>
              <li>
                <Globe size={18} />
                <div>
                  <strong>Associer à un site</strong>
                  <p>Liez le compte à un site web spécifique</p>
                </div>
              </li>
            </ul>

            <h2>Synchronisation des données</h2>
            <p>
              Les données sont synchronisées automatiquement. Vous pouvez forcer une resynchronisation
              en cliquant sur le bouton de rafraîchissement dans la bibliothèque.
            </p>
          </div>
        );

      case 'getting-started-sites':
        return (
          <div className="help-content">
            <h1>Créer des sites</h1>
            <p className="help-intro">
              Les sites représentent les propriétés web de vos clients. Ils permettent de regrouper
              plusieurs comptes publicitaires liés au même site.
            </p>

            <h2>Créer un nouveau site</h2>
            <ol className="help-numbered-list">
              <li>Accédez à <strong>Connecteurs</strong> &gt; <strong>Sites</strong></li>
              <li>Cliquez sur <strong>+ Nouveau site</strong></li>
              <li>Renseignez le nom du site et l'URL</li>
              <li>Sélectionnez le client propriétaire</li>
              <li>Enregistrez</li>
            </ol>

            <h2>Associer des comptes à un site</h2>
            <p>
              Une fois le site créé, vous pouvez y associer des comptes publicitaires depuis la bibliothèque.
              Cela permet de voir toutes les performances d'un site en un seul endroit.
            </p>

            <div className="help-tip">
              <Layers size={20} />
              <div>
                <strong>Organisation recommandée</strong>
                <p>Créez un site par propriété web distincte. Par exemple, si un client a un site e-commerce et un blog, créez deux sites séparés.</p>
              </div>
            </div>
          </div>
        );

      case 'getting-started-clients':
        return (
          <div className="help-content">
            <h1>Gérer les clients</h1>
            <p className="help-intro">
              Les clients sont vos annonceurs ou entreprises. Chaque client peut avoir plusieurs sites et comptes publicitaires.
            </p>

            <h2>Créer un client</h2>
            <ol className="help-numbered-list">
              <li>Accédez à <strong>Connecteurs</strong> &gt; <strong>Clients</strong></li>
              <li>Cliquez sur <strong>+ Nouveau client</strong></li>
              <li>Renseignez le nom du client</li>
              <li>Ajoutez optionnellement un logo et des informations de contact</li>
              <li>Enregistrez</li>
            </ol>

            <h2>Donner accès à un client</h2>
            <p>
              Vous pouvez inviter vos clients à consulter leurs données sur Kokpit avec un accès en lecture seule.
            </p>
            <ol className="help-numbered-list">
              <li>Ouvrez la fiche du client</li>
              <li>Cliquez sur <strong>Inviter</strong></li>
              <li>Entrez l'adresse email du client</li>
              <li>Le client recevra un lien pour créer son compte avec accès limité</li>
            </ol>

            <div className="help-warning">
              <Shield size={20} />
              <div>
                <strong>Accès client</strong>
                <p>Les utilisateurs avec le rôle CLIENT ont un accès en lecture seule. Ils ne peuvent pas modifier les campagnes ni accéder aux outils d'administration.</p>
              </div>
            </div>
          </div>
        );

      // DASHBOARD
      case 'dashboard-navigation':
        return (
          <div className="help-content">
            <h1>Navigation dans le Dashboard</h1>
            <p className="help-intro">
              Le Dashboard est votre vue principale pour analyser les performances de vos campagnes publicitaires.
            </p>

            <h2>Structure du Dashboard</h2>
            <ul className="help-feature-list">
              <li>
                <Filter size={18} />
                <div>
                  <strong>Sidebar gauche</strong>
                  <p>Filtrez par plateforme, période et compte. Naviguez entre vos différentes sources de données.</p>
                </div>
              </li>
              <li>
                <BarChart2 size={18} />
                <div>
                  <strong>Zone principale</strong>
                  <p>Visualisez vos KPIs, graphiques de tendances et tableaux de données détaillés.</p>
                </div>
              </li>
              <li>
                <Layers size={18} />
                <div>
                  <strong>Fil d'Ariane</strong>
                  <p>Naviguez facilement entre Client &gt; Site &gt; Compte &gt; Campagne &gt; Groupe d'annonces.</p>
                </div>
              </li>
            </ul>

            <h2>Niveaux de navigation</h2>
            <p>Le Dashboard permet de zoomer du niveau le plus global au plus détaillé :</p>
            <ol className="help-numbered-list">
              <li><strong>Vue globale</strong> - Tous vos comptes agrégés</li>
              <li><strong>Client</strong> - Tous les comptes d'un client</li>
              <li><strong>Site</strong> - Comptes liés à un site spécifique</li>
              <li><strong>Compte</strong> - Un compte publicitaire</li>
              <li><strong>Campagne</strong> - Détails d'une campagne</li>
              <li><strong>Groupe d'annonces</strong> - Mots-clés et annonces</li>
            </ol>
          </div>
        );

      case 'dashboard-metrics':
        return (
          <div className="help-content">
            <h1>Comprendre les métriques</h1>
            <p className="help-intro">
              Kokpit affiche les principales métriques de performance de vos campagnes publicitaires.
            </p>

            <h2>Métriques principales</h2>
            <div className="help-metrics-grid">
              <div className="help-metric">
                <strong>Impressions</strong>
                <p>Nombre de fois où vos annonces ont été affichées</p>
              </div>
              <div className="help-metric">
                <strong>Clics</strong>
                <p>Nombre de clics sur vos annonces</p>
              </div>
              <div className="help-metric">
                <strong>CTR</strong>
                <p>Taux de clic (Clics / Impressions × 100)</p>
              </div>
              <div className="help-metric">
                <strong>CPC</strong>
                <p>Coût par clic moyen</p>
              </div>
              <div className="help-metric">
                <strong>Coût</strong>
                <p>Dépense totale sur la période</p>
              </div>
              <div className="help-metric">
                <strong>Conversions</strong>
                <p>Actions de valeur (achats, leads, etc.)</p>
              </div>
              <div className="help-metric">
                <strong>CPA</strong>
                <p>Coût par acquisition (Coût / Conversions)</p>
              </div>
              <div className="help-metric">
                <strong>ROAS</strong>
                <p>Retour sur dépense pub (Revenu / Coût)</p>
              </div>
            </div>

            <h2>Comparaison de périodes</h2>
            <p>
              Les flèches et pourcentages à côté des métriques indiquent l'évolution par rapport
              à la période précédente :
            </p>
            <ul className="help-simple-list">
              <li><TrendingUp size={16} className="trend-up" /> Vert = amélioration</li>
              <li><TrendingUp size={16} className="trend-down" style={{ transform: 'rotate(180deg)' }} /> Rouge = dégradation</li>
            </ul>
          </div>
        );

      case 'dashboard-filters':
        return (
          <div className="help-content">
            <h1>Filtres et périodes</h1>
            <p className="help-intro">
              Utilisez les filtres de la sidebar pour affiner votre analyse.
            </p>

            <h2>Sélection de période</h2>
            <p>Le sélecteur de période permet de choisir la plage de dates à analyser :</p>
            <ul className="help-simple-list">
              <li><strong>Aujourd'hui</strong> - Données du jour en cours</li>
              <li><strong>Hier</strong> - Données de la veille</li>
              <li><strong>7 derniers jours</strong> - Dernière semaine</li>
              <li><strong>30 derniers jours</strong> - Dernier mois</li>
              <li><strong>Ce mois</strong> - Depuis le 1er du mois</li>
              <li><strong>Mois dernier</strong> - Mois précédent complet</li>
              <li><strong>Personnalisé</strong> - Choisissez vos dates</li>
            </ul>

            <h2>Filtres par plateforme</h2>
            <p>
              Cliquez sur les icônes de plateforme dans la sidebar pour filtrer par source de données :
            </p>
            <ul className="help-simple-list">
              <li>Google Ads</li>
              <li>Microsoft Ads</li>
              <li>Meta Ads</li>
              <li>Toutes les plateformes (vue agrégée)</li>
            </ul>

            <div className="help-tip">
              <Calendar size={20} />
              <div>
                <strong>Comparaison automatique</strong>
                <p>La période de comparaison est automatiquement calculée. Pour "7 derniers jours", la comparaison se fait avec les 7 jours précédents.</p>
              </div>
            </div>
          </div>
        );

      case 'dashboard-platforms':
        return (
          <div className="help-content">
            <h1>Plateformes publicitaires</h1>
            <p className="help-intro">
              Kokpit supporte les principales plateformes publicitaires avec des fonctionnalités adaptées à chacune.
            </p>

            <h2>Google Ads</h2>
            <ul className="help-simple-list">
              <li>Campagnes Search, Display, Shopping, Performance Max</li>
              <li>Groupes d'annonces et mots-clés</li>
              <li>Termes de recherche</li>
              <li>Extensions d'annonces</li>
              <li>Audiences et ciblage</li>
            </ul>

            <h2>Microsoft Ads</h2>
            <ul className="help-simple-list">
              <li>Campagnes Search et Shopping</li>
              <li>Import depuis Google Ads</li>
              <li>Audiences Microsoft</li>
              <li>Extensions</li>
            </ul>

            <h2>Meta Ads</h2>
            <ul className="help-simple-list">
              <li>Campagnes Facebook et Instagram</li>
              <li>Ensembles de publicités</li>
              <li>Publicités (créatives)</li>
              <li>Audiences personnalisées et similaires</li>
            </ul>

            <h2>Plateformes Analytics (bientôt)</h2>
            <p>
              Support prévu pour Google Analytics 4, Matomo et Piano Analytics pour croiser
              les données publicitaires avec les données de site.
            </p>
          </div>
        );

      // MCP
      case 'mcp-what-is-mcp':
        return (
          <div className="help-content">
            <h1>Qu'est-ce que le MCP ?</h1>
            <p className="help-intro">
              Le MCP (Model Context Protocol) permet d'utiliser Claude, l'IA d'Anthropic,
              pour analyser vos données publicitaires directement depuis Claude Desktop.
            </p>

            <h2>Avantages du MCP</h2>
            <div className="help-cards">
              <div className="help-card">
                <Bot size={24} />
                <h3>Analyse naturelle</h3>
                <p>Posez des questions en langage naturel sur vos campagnes</p>
              </div>
              <div className="help-card">
                <Zap size={24} />
                <h3>Actions directes</h3>
                <p>Modifiez vos campagnes directement depuis Claude</p>
              </div>
              <div className="help-card">
                <Target size={24} />
                <h3>Insights avancés</h3>
                <p>Obtenez des recommandations personnalisées</p>
              </div>
            </div>

            <h2>Exemples de questions</h2>
            <div className="help-examples">
              <div className="help-example">
                "Quelles sont mes campagnes les plus performantes ce mois-ci ?"
              </div>
              <div className="help-example">
                "Compare les performances de mes campagnes Search vs Shopping"
              </div>
              <div className="help-example">
                "Quels mots-clés ont le meilleur ROAS ?"
              </div>
              <div className="help-example">
                "Mets en pause les campagnes avec un CPA supérieur à 50€"
              </div>
            </div>
          </div>
        );

      case 'mcp-setup':
        return (
          <div className="help-content">
            <h1>Configuration du MCP</h1>
            <p className="help-intro">
              Suivez ces étapes pour connecter Kokpit à Claude Desktop.
            </p>

            <h2>Prérequis</h2>
            <ul className="help-simple-list">
              <li>Claude Desktop installé sur votre ordinateur</li>
              <li>Un abonnement Kokpit actif (Solo, Team, Agency ou Scale)</li>
              <li>Au moins un compte publicitaire connecté</li>
            </ul>

            <h2>Étapes de configuration</h2>
            <ol className="help-numbered-list">
              <li>
                <strong>Accédez à la page MCP</strong>
                <p>Cliquez sur "MCP" dans le menu principal de Kokpit</p>
              </li>
              <li>
                <strong>Copiez la configuration</strong>
                <p>Un bloc de configuration JSON est affiché. Copiez-le.</p>
              </li>
              <li>
                <strong>Ouvrez les paramètres Claude Desktop</strong>
                <p>Dans Claude Desktop, allez dans Paramètres &gt; Développeur &gt; Modifier la configuration</p>
              </li>
              <li>
                <strong>Collez la configuration</strong>
                <p>Ajoutez le bloc copié dans la section "mcpServers"</p>
              </li>
              <li>
                <strong>Redémarrez Claude Desktop</strong>
                <p>Fermez et rouvrez Claude Desktop pour appliquer les changements</p>
              </li>
            </ol>

            <div className="help-code">
              <code>{`{
  "mcpServers": {
    "kokpit": {
      "command": "npx",
      "args": ["mcp-remote", "https://api.kokpit.io/mcp"],
      "env": {
        "KOKPEET_ACCESS_TOKEN": "votre-token"
      }
    }
  }
}`}</code>
            </div>

            <div className="help-warning">
              <Shield size={20} />
              <div>
                <strong>Sécurité</strong>
                <p>Ne partagez jamais votre token d'accès. Il donne accès à vos données publicitaires.</p>
              </div>
            </div>
          </div>
        );

      case 'mcp-tools':
        return (
          <div className="help-content">
            <h1>Outils MCP disponibles</h1>
            <p className="help-intro">
              Le serveur MCP Kokpit expose plusieurs outils que Claude peut utiliser pour accéder à vos données.
            </p>

            <h2>Outils de lecture</h2>
            <div className="help-tools-list">
              <div className="help-tool">
                <code>list_sources</code>
                <p>Liste tous vos comptes publicitaires connectés avec leurs métadonnées</p>
              </div>
              <div className="help-tool">
                <code>get_fields</code>
                <p>Récupère les champs disponibles pour une source (métriques, dimensions)</p>
              </div>
              <div className="help-tool">
                <code>get_data</code>
                <p>Récupère les données de performance selon vos critères (dates, filtres, métriques)</p>
              </div>
              <div className="help-tool">
                <code>get_schema</code>
                <p>Obtient le schéma complet des données disponibles</p>
              </div>
              <div className="help-tool">
                <code>generate_keyword_ideas</code>
                <p>Génère des idées de mots-clés via l'API Google Ads</p>
              </div>
              <div className="help-tool">
                <code>list_notes</code>
                <p>Liste les notes associées à vos comptes et campagnes</p>
              </div>
            </div>

            <h2>Utilisation</h2>
            <p>
              Vous n'avez pas besoin d'appeler ces outils directement. Posez simplement vos questions
              à Claude et il utilisera automatiquement les bons outils.
            </p>

            <div className="help-example">
              Vous : "Montre-moi les performances de mon compte Google Ads 123-456-7890"<br />
              Claude : <em>utilise list_sources puis get_data pour répondre</em>
            </div>
          </div>
        );

      case 'mcp-write-operations':
        return (
          <div className="help-content">
            <h1>Opérations d'écriture</h1>
            <p className="help-intro">
              Le MCP permet également de modifier vos campagnes directement depuis Claude (Google Ads uniquement pour le moment).
            </p>

            <h2>Opérations disponibles</h2>
            <div className="help-tools-list">
              <div className="help-tool">
                <code>update_campaign_status</code>
                <p>Activer ou mettre en pause une campagne</p>
              </div>
              <div className="help-tool">
                <code>update_campaign_budget</code>
                <p>Modifier le budget journalier d'une campagne</p>
              </div>
              <div className="help-tool">
                <code>update_ad_group_status</code>
                <p>Activer ou mettre en pause un groupe d'annonces</p>
              </div>
              <div className="help-tool">
                <code>add_negative_keywords</code>
                <p>Ajouter des mots-clés négatifs à une campagne</p>
              </div>
              <div className="help-tool">
                <code>add_keywords</code>
                <p>Ajouter des mots-clés positifs à un groupe d'annonces</p>
              </div>
              <div className="help-tool">
                <code>create_responsive_search_ad</code>
                <p>Créer une nouvelle annonce responsive</p>
              </div>
            </div>

            <div className="help-warning">
              <Shield size={20} />
              <div>
                <strong>Sécurité des écritures</strong>
                <p>Les opérations d'écriture sont protégées par une whitelist. Seuls les comptes explicitement autorisés peuvent être modifiés. Contactez le support pour activer l'écriture sur vos comptes.</p>
              </div>
            </div>

            <h2>Exemples</h2>
            <div className="help-examples">
              <div className="help-example">
                "Mets en pause la campagne 'Brand - France'"
              </div>
              <div className="help-example">
                "Augmente le budget de ma campagne Shopping à 100€/jour"
              </div>
              <div className="help-example">
                "Ajoute 'gratuit' comme mot-clé négatif sur toutes mes campagnes"
              </div>
            </div>
          </div>
        );

      // GESTION
      case 'management-subscription':
        return (
          <div className="help-content">
            <h1>Gérer son abonnement</h1>
            <p className="help-intro">
              Kokpit propose plusieurs plans d'abonnement adaptés à vos besoins.
            </p>

            <h2>Plans disponibles</h2>
            <div className="help-plans">
              <div className="help-plan">
                <h3>Free</h3>
                <p>Gratuit</p>
                <ul>
                  <li>1 compte publicitaire</li>
                  <li>Dashboard basique</li>
                  <li>7 jours d'historique</li>
                </ul>
              </div>
              <div className="help-plan">
                <h3>Solo</h3>
                <p>19€/mois</p>
                <ul>
                  <li>5 comptes publicitaires</li>
                  <li>Dashboard complet</li>
                  <li>MCP inclus</li>
                  <li>90 jours d'historique</li>
                </ul>
              </div>
              <div className="help-plan">
                <h3>Team</h3>
                <p>69€/mois</p>
                <ul>
                  <li>25 comptes publicitaires</li>
                  <li>3 utilisateurs</li>
                  <li>Toutes les fonctionnalités</li>
                </ul>
              </div>
              <div className="help-plan">
                <h3>Agency</h3>
                <p>179€/mois</p>
                <ul>
                  <li>100 comptes publicitaires</li>
                  <li>10 utilisateurs</li>
                  <li>Accès clients</li>
                  <li>Support prioritaire</li>
                </ul>
              </div>
            </div>

            <h2>Changer de plan</h2>
            <ol className="help-numbered-list">
              <li>Accédez à <strong>Abonnement</strong> dans le menu</li>
              <li>Cliquez sur <strong>Changer de plan</strong></li>
              <li>Sélectionnez le nouveau plan souhaité</li>
              <li>Confirmez le changement</li>
            </ol>

            <div className="help-tip">
              <CreditCard size={20} />
              <div>
                <strong>Facturation</strong>
                <p>Les changements de plan prennent effet immédiatement. Un prorata est calculé pour la période en cours.</p>
              </div>
            </div>
          </div>
        );

      case 'management-statistics':
        return (
          <div className="help-content">
            <h1>Consulter les statistiques</h1>
            <p className="help-intro">
              La page Statistiques vous donne une vue d'ensemble de votre utilisation de Kokpit.
            </p>

            <h2>Métriques d'utilisation</h2>
            <ul className="help-feature-list">
              <li>
                <Database size={18} />
                <div>
                  <strong>Comptes connectés</strong>
                  <p>Nombre de comptes publicitaires actifs</p>
                </div>
              </li>
              <li>
                <Users size={18} />
                <div>
                  <strong>Utilisateurs</strong>
                  <p>Membres de votre équipe</p>
                </div>
              </li>
              <li>
                <Building size={18} />
                <div>
                  <strong>Clients</strong>
                  <p>Nombre de clients gérés</p>
                </div>
              </li>
              <li>
                <Globe size={18} />
                <div>
                  <strong>Sites</strong>
                  <p>Propriétés web configurées</p>
                </div>
              </li>
            </ul>

            <h2>Accéder aux statistiques</h2>
            <p>
              Cliquez sur votre nom en haut à droite, puis sélectionnez <strong>Statistiques</strong>
              dans le menu déroulant.
            </p>
          </div>
        );

      case 'management-notifications':
        return (
          <div className="help-content">
            <h1>Paramétrer les notifications</h1>
            <p className="help-intro">
              Configurez des alertes pour être informé des événements importants sur vos comptes.
            </p>

            <h2>Types de notifications</h2>
            <ul className="help-feature-list">
              <li>
                <Bell size={18} />
                <div>
                  <strong>Alertes de performance</strong>
                  <p>Soyez notifié quand une métrique dépasse un seuil</p>
                </div>
              </li>
              <li>
                <TrendingUp size={18} />
                <div>
                  <strong>Variations importantes</strong>
                  <p>Détectez les hausses ou baisses significatives</p>
                </div>
              </li>
              <li>
                <Shield size={18} />
                <div>
                  <strong>Problèmes de compte</strong>
                  <p>Erreurs de synchronisation, comptes suspendus</p>
                </div>
              </li>
            </ul>

            <h2>Configurer une notification</h2>
            <ol className="help-numbered-list">
              <li>Accédez à <strong>Notifications</strong> dans le menu</li>
              <li>Cliquez sur <strong>+ Nouvelle alerte</strong></li>
              <li>Sélectionnez le type d'alerte et les conditions</li>
              <li>Choisissez le canal de notification (email, in-app)</li>
              <li>Enregistrez</li>
            </ol>
          </div>
        );

      case 'management-team':
        return (
          <div className="help-content">
            <h1>Équipe et organisation</h1>
            <p className="help-intro">
              Gérez les membres de votre équipe et les permissions d'accès.
            </p>

            <h2>Rôles disponibles</h2>
            <div className="help-roles">
              <div className="help-role">
                <strong>Propriétaire (Owner)</strong>
                <p>Accès complet, gestion de la facturation et des membres</p>
              </div>
              <div className="help-role">
                <strong>Administrateur (Admin)</strong>
                <p>Accès complet sauf facturation</p>
              </div>
              <div className="help-role">
                <strong>Membre (Member)</strong>
                <p>Accès aux dashboards et analyses</p>
              </div>
              <div className="help-role">
                <strong>Lecteur (Viewer)</strong>
                <p>Accès en lecture seule</p>
              </div>
            </div>

            <h2>Inviter un membre</h2>
            <ol className="help-numbered-list">
              <li>Accédez à <strong>Organisation</strong> dans le menu</li>
              <li>Onglet <strong>Équipe</strong></li>
              <li>Cliquez sur <strong>Inviter</strong></li>
              <li>Entrez l'email et sélectionnez le rôle</li>
              <li>L'invité recevra un email avec un lien pour rejoindre</li>
            </ol>

            <h2>Gérer les permissions</h2>
            <p>
              Les permissions peuvent être affinées par client ou par compte.
              Un membre peut avoir accès à certains clients seulement.
            </p>

            <div className="help-tip">
              <Users size={20} />
              <div>
                <strong>Limite d'utilisateurs</strong>
                <p>Le nombre d'utilisateurs est limité selon votre plan. Passez à un plan supérieur pour ajouter plus de membres.</p>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="help-content">
            <h1>Aide</h1>
            <p>Sélectionnez une section dans le menu de gauche.</p>
          </div>
        );
    }
  };

  return (
    <div className="help-center">
      <div className="help-header">
        <h1>
          <BookOpen size={24} />
          Centre d'aide Kokpit
        </h1>
      </div>

      <div className="help-layout">
        <nav className="help-nav">
          {categories.map(category => {
            const Icon = category.icon;
            const isActive = activeCategory === category.id;

            return (
              <div key={category.id} className="help-nav-category">
                <button
                  className={`help-nav-category-btn ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setActiveSection(category.sections[0].id);
                  }}
                >
                  <Icon size={18} />
                  <span>{category.label}</span>
                  <ChevronRight size={16} className={`chevron ${isActive ? 'open' : ''}`} />
                </button>

                {isActive && (
                  <div className="help-nav-sections">
                    {category.sections.map(section => (
                      <button
                        key={section.id}
                        className={`help-nav-section-btn ${activeSection === section.id ? 'active' : ''}`}
                        onClick={() => setActiveSection(section.id)}
                      >
                        {section.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <main className="help-main">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default HelpCenter;
